import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import {
  Heart, Lightbulb, Compass, Shield, Flame, Eye, Sun, Zap,
} from "lucide-react";

const values = [
  { name: "Vā Fealoa'i", meaning: "Sacred Relational Space", description: "Holds to the sacred relational space between people; the starting point for all interactions, partnerships, and programmes.", icon: Heart },
  { name: "Toi Whakahou", meaning: "Creative Growth", description: "Keeps us growing; embeds creativity and continuous improvement into our work, thinking, and leadership.", icon: Lightbulb },
  { name: "Whakamātau", meaning: "Strategic Experimentation", description: "Gives freedom and discipline to experiment; plan with foresight and navigate complexity with strategic intent.", icon: Compass },
  { name: "Lototō", meaning: "Grounded Resilience", description: "Grounds us; provides a steady, resilient heart for wise and courageous action, free from ego, deeply respectful of the collective.", icon: Shield },
  { name: "Māia", meaning: "Inner Courage", description: "Calls to courage; inner strength to stand firm, speak up, persevere, and act with conviction, especially in challenging circumstances.", icon: Flame },
  { name: "Yalomatua", meaning: "Deep Wisdom", description: "Our compass; deep wisdom, emotional intelligence, and grounded decision-making across all domains and actions.", icon: Eye },
  { name: "Motutaga Tau Mafola", meaning: "Community Wellbeing", description: "Our purpose made clear; measures success by the wellbeing and peace of the community above all else.", icon: Sun },
  { name: "Hihiri", meaning: "The Energising Fire", description: "The fire that keeps us moving; the energising force that motivates and inspires within the organisation, partnerships, and communities.", icon: Zap },
];

export default function ValuesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [selected, setSelected] = useState(null);
  const [paused, setPaused] = useState(false);
  const angleRef = useRef(0);
  const orbitRef = useRef(null);
  const nodesRef = useRef([]);

  // Smooth orbit using direct DOM manipulation - refs prevent stale closures
  useEffect(() => {
    let animId;
    const radiusX = 580;
    const radiusY = 80;

    const tick = () => {
      if (!paused && selected === null) {
        angleRef.current = (angleRef.current + 0.12) % 360;
      }

      nodesRef.current.forEach((node, i) => {
        if (!node) return;
        const itemAngle = (angleRef.current + (i * 360 / 8)) * (Math.PI / 180);
        const x = radiusX * Math.cos(itemAngle);
        const y = radiusY * Math.sin(itemAngle);
        const depth = (Math.sin(itemAngle) + 1) / 2;
        const scale = selected === i ? 1.15 : 0.65 + depth * 0.45;
        const opacity = selected !== null && selected !== i ? 0.2 : 0.5 + depth * 0.5;
        const z = Math.round(depth * 10);

        node.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${scale})`;
        node.style.opacity = opacity;
        node.style.zIndex = selected === i ? 20 : z;
      });

      animId = requestAnimationFrame(tick);
    };

    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [paused, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (i) => {
    setSelected(selected === i ? null : i);
  };

  return (
    <section
      id="values"
      data-testid="values-section"
      className="relative py-6 sm:py-8 overflow-hidden"
    >
      <div ref={ref} className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="mb-6 text-center"
          >
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#bb321a] mb-4">
              Our Living Values
            </p>
            <h2 className="font-['Syne'] text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
              Eight <span className="text-[#bb321a]">Values</span>
            </h2>
          </motion.div>
        </div>

        {/* Orbit container - desktop - compact horizontal */}
        <div
          className="hidden lg:block relative mx-auto"
          style={{ width: "1280px", height: "340px" }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => { setPaused(false); }}
        >
          {/* Orbit path - subtle horizontal ellipse */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border border-white/[0.03] rounded-full"
            style={{ width: 580 * 2 + 60, height: 80 * 2 + 60 }}
          />

          {/* Center label */}
          {selected === null && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
              <p className="font-['Syne'] text-lg font-bold text-white/15">T.I.I.N.A.</p>
            </div>
          )}

          {/* Selected detail - centre */}
          {selected !== null && (
            <motion.div
              key={selected}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] text-center z-30 bg-black/60 backdrop-blur-md p-6 rounded-sm"
            >
              <p className="font-['Syne'] text-xl font-bold text-white mb-1">{values[selected].name}</p>
              <p className="text-xs font-semibold text-white uppercase tracking-wider mb-3">{values[selected].meaning}</p>
              <p className="text-sm text-white/70 leading-relaxed">{values[selected].description}</p>
              <button onClick={() => setSelected(null)} className="mt-3 text-xs text-white/30 hover:text-white/60">Click to close</button>
            </motion.div>
          )}

          {/* Orbiting value nodes */}
          {values.map((value, i) => {
            const Icon = value.icon;
            const isSelected = selected === i;

            return (
              <div
                key={value.name}
                ref={el => nodesRef.current[i] = el}
                data-testid={`value-orbit-${i}`}
                onClick={() => handleClick(i)}
                className="absolute cursor-pointer"
                style={{
                  left: "50%",
                  top: "50%",
                  willChange: "transform, opacity",
                }}
              >
                <div className={`flex items-center gap-4 px-6 py-5 rounded-full transition-all duration-300 ${
                  isSelected
                    ? "bg-[#bb321a] shadow-lg shadow-[#bb321a]/20"
                    : "bg-white/10 backdrop-blur-sm hover:bg-white/15"
                }`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isSelected ? "bg-white/20" : "bg-white/10"
                  }`}>
                    <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-base font-bold text-white whitespace-nowrap">{value.name}</p>
                    <p className={`text-xs whitespace-nowrap ${isSelected ? "text-white/80" : "text-[#bb321a]"}`}>{value.meaning}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile fallback - horizontal scroll */}
        <div className="lg:hidden flex gap-4 overflow-x-auto pb-4 pl-4 sm:pl-8 pr-12 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: "touch" }}>
          {values.map((value, i) => {
            const Icon = value.icon;
            return (
              <div
                key={value.name}
                data-testid={`value-mobile-${i}`}
                className="flex-shrink-0 w-[260px] sm:w-[300px] snap-center bg-white/10 backdrop-blur-sm p-5"
                style={{ borderTop: "3px solid transparent", borderImage: "linear-gradient(90deg, #364225, #485e23, #bb321a) 1" }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-['Syne'] text-base font-bold text-white">{value.name}</h4>
                    <p className="text-[10px] font-semibold text-[#bb321a] uppercase tracking-wider">{value.meaning}</p>
                  </div>
                </div>
                <p className="text-white/70 text-xs leading-relaxed">{value.description}</p>
                <p className="mt-3 text-xs text-white/30 font-bold">{String(i + 1).padStart(2, "0")} / 08</p>
              </div>
            );
          })}
        </div>

        <p className="text-center text-white/20 text-xs mt-4 hidden lg:block">
          {selected !== null ? "Click a value to close" : "Hover to pause. Click a value to explore."}
        </p>
      </div>
    </section>
  );
}
